/* eslint-disable no-constructor-return */
import { map, switchMap, share } from 'rxjs/operators';
import * as R from 'ramda';
import { combineLatest, Observable, of } from 'rxjs';
import { PercentAmount } from '@akropolis-web/primitives';

import {
  AccountMetrics,
  NetworkSlug,
  Position,
  PositionHistoryItem,
  ProductSlug,
  VersionSlug,
  asNetworkSlug,
  asProductSlug,
  asVersionSlug,
} from 'domain/types';
import { memoize } from 'utils/decorators';
import { resetOnZeroRefs } from 'utils/rxjs';
import { sumEitherUsdBalances, sumUsdBalances, toTotalBalanceUSD } from 'domain/utils';

import { convertPosition, convertPositionHistory } from './converters';
import { apiErrorInterceptor } from './apiErrorInterceptor';
import { priceApi } from './priceApi';
import { schemas } from './apostro-rest/types';
import { loadPositionHistory, loadPositions } from './apostro-rest/public';
import type { PositionRequestArgs } from './apostro-rest/public';
import { globalApi } from './globalApi';

/* eslint-disable class-methods-use-this */
class PositionsApi {
  constructor() {
    return apiErrorInterceptor.getProxiedObj(this);
  }

  @memoize((...args: string[]) => R.toString(args))
  public getAccountMetrics$({
    account,
    productSlug,
    versionSlug,
    network,
    period,
  }: {
    account: string;
    productSlug?: ProductSlug;
    versionSlug?: VersionSlug;
    network?: NetworkSlug;
    period: 'latest' | 'dayAgo';
  }): Observable<AccountMetrics | null> {
    return this.getAccountPositions$({
      account,
      network,
      productSlug,
      versionSlug,
      period,
    }).pipe(
      map(positions => {
        if (!positions.length) {
          return null;
        }

        const lastActive = new Date(Math.max(...positions.map(p => p.lastActive.getTime())));

        const balances = R.pluck('balanceUsd', positions);
        const partialBalances = R.pluck('partialBalanceUSD', positions);

        const totalBalanceUSD = toTotalBalanceUSD(sumEitherUsdBalances(balances));
        const partialTotalBalanceUSD = toTotalBalanceUSD(sumUsdBalances(partialBalances));

        const overallLtv = totalBalanceUSD.map(({ totalCollateral, totalDebt }) =>
          totalCollateral.isZero()
            ? new PercentAmount(0)
            : new PercentAmount(100).mul(totalDebt).div(totalCollateral),
        );

        const metrics: AccountMetrics = {
          lastActive,
          totalBalanceUSD,
          partialTotalBalanceUSD,
          overallLtv,
        };

        return metrics;
      }),
    );
  }

  @memoize((...args: any[]) => R.toString(args))
  public getPositions$(
    args: PositionRequestArgs,
    period: 'latest' | 'dayAgo',
  ): Observable<{ data: Position[]; total: number }> {
    return globalApi.getDataTimestamp$(period).pipe(
      switchMap(timestamp => loadPositions({ ...args, timestamp })),
      switchMap(response =>
        combineLatest([this.convertPositions$(response.data), of(response.total)]),
      ),
      map(([data, total]) => ({
        data: [...data].sort((a, b) => b.lastActive.getTime() - a.lastActive.getTime()),
        total,
      })),
      share(resetOnZeroRefs()),
    );
  }

  @memoize((...args: any[]) => R.toString(args))
  public getPosition$({
    account,
    productSlug,
    versionSlug,
    network,
    marketId,
    period,
  }: {
    account: string;
    productSlug: ProductSlug;
    versionSlug: VersionSlug;
    network: NetworkSlug;
    marketId: string;
    period: 'latest' | 'dayAgo';
  }): Observable<Position | null> {
    return this.getAccountPositions$({
      account,
      productSlug,
      versionSlug,
      network,
      marketId,
      period,
    }).pipe(map(positions => (positions.length === 1 ? positions[0] : null)));
  }

  @memoize((...args: string[]) => R.toString(args))
  public getPositionHistory$({
    account,
    productSlug,
    versionSlug,
    network,
    marketId,
    limit,
    offset,
  }: {
    account: string;
    productSlug: ProductSlug;
    versionSlug: VersionSlug;
    network: NetworkSlug;
    marketId: string;
    limit: number | undefined;
    offset: number | undefined;
  }): Observable<{ data: PositionHistoryItem[]; total: number }> {
    return loadPositionHistory({
      account_vid: account,
      product_slug: productSlug,
      version_slug: versionSlug,
      network_slug: network,
      market_vid: marketId,
      limit,
      offset,
    }).pipe(
      map(response => ({
        data: convertPositionHistory(response),
        total: response.total,
      })),
      share(resetOnZeroRefs()),
    );
  }

  @memoize((...args: string[]) => R.toString(args))
  private getAccountPositions$({
    account,
    productSlug,
    versionSlug,
    network,
    marketId,
    period,
  }: {
    account: string;
    productSlug?: ProductSlug;
    versionSlug?: VersionSlug;
    network?: NetworkSlug;
    marketId?: string;
    period: 'latest' | 'dayAgo';
  }): Observable<Position[]> {
    return combineLatest([
      this.getPositions$(
        {
          account_vid: account,
          network_slug: network,
          product_slug: productSlug,
          version_slug: versionSlug,
          market_vid: marketId,
          is_archived: false,
          limit: 10000,
        },
        period,
      ),
      this.getPositions$(
        {
          account_vid: account,
          network_slug: network,
          product_slug: productSlug,
          version_slug: versionSlug,
          market_vid: marketId,
          is_archived: true,
          limit: 10000,
        },
        period,
      ),
    ]).pipe(
      map(([active, archived]) => [...active.data, ...archived.data]),
      map(positions => positions.sort((a, b) => b.lastActive.getTime() - a.lastActive.getTime())),
    );
  }

  private convertPositions$(serverPositions: schemas['Position'][]) {
    if (!serverPositions.length) {
      return of([]);
    }

    const positionsGroups = Object.entries(R.groupBy(x => x.product_slug, serverPositions))
      .map(([productSlug, productPositions]) =>
        Object.entries(R.groupBy(x => x.version_slug, productPositions)).map(
          ([versionSlug, versionPositions]) =>
            Object.entries(R.groupBy(v => v.network, versionPositions)).map(
              ([network, networkPositions]) =>
                [
                  asProductSlug(productSlug),
                  asVersionSlug(versionSlug),
                  asNetworkSlug(network),
                  networkPositions,
                ] as const,
            ),
        ),
      )
      .flat(2);

    return combineLatest(
      positionsGroups.map(([productSlug, versionSlug, network, protocolPositions]) => {
        const tokens = R.uniq(
          protocolPositions.map(p => p.balances.map(pPart => pPart.token.address)).flat(),
        );

        return priceApi
          .getTokenPrices$({ productSlug, versionSlug, network, assets: tokens })
          .pipe(
            map(prices => protocolPositions.map(position => convertPosition(position, prices))),
          );
      }),
    ).pipe(map(positions => positions.flat()));
  }
}

export const positionsApi = new PositionsApi();
